// Player Card Types for New Lineup Mode System

import type { Player } from './core';

// ============================================================================
// Player Card Core Types
// ============================================================================

/** Baseball/Softball-specific last level played options */
export const LAST_LEVEL_PLAYED_BASEBALL_SOFTBALL = [
  'Tee-Ball',
  'Rec-League',
  'Competitive Rec',
  'Travel-A',
  'Travel-AA',
  'Travel-AAA',
  'Majors/Elite',
] as const;

/** Options for other sports (e.g. football, basketball) */
export const LAST_LEVEL_PLAYED_OTHER_SPORTS = [
  'Rec League',
  'Travel League',
] as const;

export type LastLevelPlayedBaseballSoftball = (typeof LAST_LEVEL_PLAYED_BASEBALL_SOFTBALL)[number];
export type LastLevelPlayedOtherSports = (typeof LAST_LEVEL_PLAYED_OTHER_SPORTS)[number];
export type LastLevelPlayed = LastLevelPlayedBaseballSoftball | LastLevelPlayedOtherSports;

/** Get the options for last level played based on sport */
export function getLastLevelPlayedOptions(sport: string | undefined): readonly string[] {
  if (sport === 'Baseball' || sport === 'Softball') {
    return LAST_LEVEL_PLAYED_BASEBALL_SOFTBALL;
  }
  return LAST_LEVEL_PLAYED_OTHER_SPORTS;
}

export interface PlayerCard {
  id: string;
  player_id: string;
  team_id: string;
  
  // Core Identity
  age?: number;
  grade?: string;
  photo_url?: string;
  avatar_url?: string;
  nickname?: string;
  /** Free-text self-description, editable from the individual persona's Dashboard profile card. */
  bio?: string;
  characteristic_trait?: string; // Legacy field - kept for backward compatibility
  offense_designation?: string; // Offensive characteristic trait
  defense_designation?: string; // Defensive characteristic trait
  /** Last level played (baseball/softball: Tee-Ball through Majors/Elite; other sports: Rec League, Travel League) */
  last_level_played?: LastLevelPlayed;
  
  // Position Eligibility
  eligible_positions: string[];
  role_types: string[];
  
  // Football-specific
  can_play_line: boolean;
  can_play_skill: boolean;
  offense_only: boolean;
  defense_only: boolean;
  line_only: boolean;
  skill_only: boolean;
  max_plays_both_ways?: number;
  no_contact: boolean;
  limited_contact: boolean;
  is_new_player: boolean;
  
  // Health
  health_status: 'healthy' | 'limited' | 'no_contact';
  health_notes?: string;
  health_updated_at?: string;
  
  // Rating
  overall_rating?: number; // 0-10 scale (legacy)
  ovr_rating?: number; // 0-100 scale (Madden-style)
  rating_breakdown?: RatingBreakdown;
  
  // Handedness/Preference (sport-specific)
  throwing_hand?: 'L' | 'R'; // Baseball/Softball: throwing hand
  batting_hand?: 'L' | 'R' | 'S'; // Baseball/Softball: batting hand (Left, Right, Switch)
  handedness?: 'L' | 'R'; // Generic handedness for other sports
  shooting_preference?: 'L' | 'R'; // Soccer/Hockey: shooting preference
  
  created_at: string;
  updated_at: string;
  
  // Related data (loaded separately)
  player?: Player;
  attributes?: PlayerAttribute[];
  goals?: PlayerGoal[];
  notes?: PlayerDevelopmentNote[];
  attendance_stats?: AttendanceStats;
}

export interface PlayerAttribute {
  id: string;
  player_card_id: string;
  sport: string;
  category: 'physical' | 'technical' | 'mental';
  attribute_key: string;
  attribute_name: string;
  value: number; // 1-10
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PlayerGoal {
  id: string;
  player_card_id: string;
  goal_text: string;
  category: 'skill' | 'physical' | 'mental' | 'team';
  target_date?: string;
  status: 'not_started' | 'in_progress' | 'achieved' | 'cancelled';
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PlayerDevelopmentNote {
  id: string;
  player_card_id: string;
  created_by?: string;
  note_text: string;
  category?: string;
  tags?: string[];
  created_at: string;
}

export interface PlayerAttendance {
  id: string;
  player_id: string;
  team_id: string;
  event_type: 'practice' | 'game';
  event_id: string;
  event_date: string;
  event_time?: string;
  status: 'late' | 'no_show' | 'excused'; // Only exceptions are tracked (default is present)
  arrival_time?: string;
  departure_time?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface AttendanceStats {
  total_practices: number;
  total_games: number;
  practices_present: number; // Calculated: total - exceptions
  practices_late: number; // Derogatory
  practices_no_show: number; // Derogatory
  practices_excused: number; // Not derogatory
  games_present: number; // Calculated: total - exceptions
  games_late: number;
  games_no_show: number;
  games_excused: number;
  attendance_percentage: number;
  last_attended?: string;
}

/** One point in a team's attendance trend over time (chronological, past events only). */
export interface AttendanceTrendPoint {
  event_date: string;
  event_type: 'practice' | 'game';
  attendance_percentage: number;
}

/** A player flagged as at-risk on attendance (below the at-risk threshold). */
export interface AtRiskPlayer {
  player_id: string;
  first_name: string;
  last_name: string;
  number: number | null;
  attendance_percentage: number;
}

/** Team-wide attendance rollup for the Attendance Report (see playerAttendanceService.getTeamAttendanceSummary). */
export interface TeamAttendanceSummary {
  total_practices: number;
  total_games: number;
  overall_attendance_percentage: number;
  players: Array<{
    player_id: string;
    first_name: string;
    last_name: string;
    number: number | null;
    stats: AttendanceStats;
  }>;
  trend: AttendanceTrendPoint[];
  at_risk_players: AtRiskPlayer[];
}

export interface RatingBreakdown {
  physical_score: number;
  technical_score: number;
  mental_score: number;
  attendance_factor: number;
  overall: number;
  last_calculated: string;
}

// ============================================================================
// Formation Spot Types
// ============================================================================

export interface FormationSpot {
  id: string;
  team_id: string;
  formation_id?: string;
  formation_name: string;
  sport: string;
  unit: 'Offense' | 'Defense' | 'Special Teams' | 'Lineup' | 'Fielding';
  label: string; // Coach-defined label
  position_key?: string; // Optional standard position key
  role_type: string; // 'line', 'skill', 'battery', etc.
  role_tags: string[];
  x_coordinate?: number;
  y_coordinate?: number;
  order_index: number;
  primary_player_id?: string;
  backup_player_id?: string;
  third_string_player_id?: string;
  is_required: boolean;
  min_depth: number;
  max_depth: number;
  created_at: string;
  updated_at: string;
  
  // Loaded separately
  primary_player?: Player;
  backup_player?: Player;
  third_string_player?: Player;
}

export interface LineupModeSettings {
  id: string;
  team_id: string;
  lineup_mode: 'legacy' | 'player_card_v1';
  enabled_at?: string;
  enabled_by?: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Sport-Specific Role Type Definitions
// ============================================================================

export const SPORT_ROLE_TYPES: Record<string, string[]> = {
  Football: ['line', 'skill'],
  FlagFootball: ['line', 'skill'],
  Baseball: ['battery', 'infield', 'outfield'],
  Softball: ['battery', 'infield', 'outfield'],
  Basketball: ['guard', 'wing', 'big'],
  Soccer: ['goalkeeper', 'defender', 'midfielder', 'forward'],
  Volleyball: ['setter', 'hitter', 'blocker', 'defender'],
  Hockey: ['forward', 'defenseman', 'goalie'],
  Lacrosse: ['attack', 'midfield', 'defense', 'goalie'],
};

// ============================================================================
// Attribute Definitions by Sport
// ============================================================================

// Attribute definitions structure
// Physical and mental are universal across all sports
// Technical attributes are sport-specific
type TechnicalAttributeDef = string[] | { required: string[]; optional: string[] };

export const ATTRIBUTE_DEFINITIONS: Record<string, Record<string, string[] | TechnicalAttributeDef>> = {
  physical: {
    all: ['speed', 'strength', 'agility', 'stamina', 'toughness'],
  },
  technical: {
    // Football: Core attributes required for all, optional position-specific attributes
    Football: {
      required: ['tackling', 'blocking', 'catching', 'route_running', 'ball_security', 'awareness'],
      optional: ['snapping', 'throwing_power', 'throwing_accuracy'],
    },
    FlagFootball: ['tackling', 'blocking', 'catching', 'route_running', 'ball_security', 'awareness'],
    // Baseball/Softball: Shared attribute definitions (same for both sports)
    // "hands" = general catching/fielding ability (applies to all positions)
    // "catching" = position-specific catcher skills (receiving, blocking, framing)
    Baseball: {
      required: ['throwing_accuracy', 'throwing_power', 'glove', 'infielding', 'outfielding', 'batting_power', 'batting_contact', 'base_running'],
      optional: ['pitching', 'catching'],
    },
    Softball: {
      required: ['throwing_accuracy', 'throwing_power', 'glove', 'infielding', 'outfielding', 'batting_power', 'batting_contact', 'base_running'],
      optional: ['pitching', 'catching'],
    },
    Basketball: ['shooting', 'dribbling', 'passing', 'rebounding', 'defense', 'stealing', 'blocking'],
    Soccer: ['passing', 'dribbling', 'shooting', 'tackling', 'positioning'],
    Volleyball: ['serving', 'spiking', 'setting', 'blocking', 'digging'],
    Hockey: ['skating', 'shooting', 'passing', 'checking', 'positioning'],
    Lacrosse: ['shooting', 'passing', 'catching', 'ground_balls', 'defense'],
  },
  mental: {
    all: ['coachability', 'focus', 'attitude', 'sport_iq'],
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get attribute definitions for a sport
 * Returns required and optional technical attributes separately for sports that support it
 */
export function getAttributeDefinitions(sport: string): {
  physical: string[];
  technical: string[];
  technicalRequired?: string[];
  technicalOptional?: string[];
  mental: string[];
} {
  const technicalDef = ATTRIBUTE_DEFINITIONS.technical[sport];
  
  // Handle sports with required/optional structure (Football, Baseball, Softball)
  if (technicalDef && typeof technicalDef === 'object' && !Array.isArray(technicalDef) && 'required' in technicalDef) {
    const sportDef = technicalDef as { required: string[]; optional: string[] };
    const physicalAttrs: string[] = ATTRIBUTE_DEFINITIONS.physical.all as string[];
    const mentalAttrs: string[] = ATTRIBUTE_DEFINITIONS.mental.all as string[];
    return {
      physical: physicalAttrs,
      technical: [...sportDef.required, ...sportDef.optional], // All for backward compatibility
      technicalRequired: [...sportDef.required],
      technicalOptional: [...sportDef.optional],
      mental: mentalAttrs,
    };
  }
  
  // Handle other sports (array format)
  const technicalArray: string[] = Array.isArray(technicalDef) ? [...technicalDef] : [];
  const physicalAttrs: string[] = ATTRIBUTE_DEFINITIONS.physical.all as string[];
  const mentalAttrs: string[] = ATTRIBUTE_DEFINITIONS.mental.all as string[];
  
  return {
    physical: physicalAttrs,
    technical: technicalArray,
    mental: mentalAttrs,
  };
}

/**
 * Get only required technical attributes for a sport
 */
export function getRequiredTechnicalAttributes(sport: string): string[] {
  const defs = getAttributeDefinitions(sport);
  return defs.technicalRequired || defs.technical;
}

/**
 * Get optional technical attributes for a sport
 */
export function getOptionalTechnicalAttributes(sport: string): string[] {
  const defs = getAttributeDefinitions(sport);
  return defs.technicalOptional || [];
}

/**
 * Get role types for a sport
 */
export function getRoleTypesForSport(sport: string): string[] {
  return SPORT_ROLE_TYPES[sport] || [];
}

/**
 * Check if player card is eligible for a role type
 */
export function isEligibleForRole(card: PlayerCard, roleType: string): boolean {
  return card.role_types.includes(roleType);
}

/**
 * Check if player can play a position
 */
export function canPlayPosition(card: PlayerCard, positionKey: string): boolean {
  return card.eligible_positions.includes(positionKey);
}

